import { trigger, state, style, transition, animate } from '@angular/animations';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit, Inject, ViewChild, HostListener } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { DialogConfirmComponent } from 'src/app/dialog/dialog-confirm/dialog-confirm.component';
import { AppService, GRSSResponse, GRSSUserRoles, ValueIdPair } from 'src/app/_services/app.service';
import { AddAccountComponent } from './add-account/add-account.component';
import { AddServiceAccountComponent } from './add-service-account/add-service-account.component';
import { AddUserAccountComponent } from './add-user-account/add-user-account.component';
import { ComponentCanDeactivate } from 'src/app/_guard/pending-changes.guard';
import { Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-manage-accounts',
    templateUrl: './manage-accounts.component.html',
    styleUrls: ['./manage-accounts.component.css'],
    animations: [
        trigger('slider', [
            state('*', style({
                height: 0
            })),
            state('show', style({
                height: '*'
            })),
            transition('* <=> show', [animate('400ms cubic-bezier(0.25, 0.8, 0.25, 1)')])
        ])
    ],
    standalone: false
})
export class ManageAccountsComponent implements OnInit, ComponentCanDeactivate {

  displayColumns: string[] = ['username', 'firstName', 'lastName', 'email', 'phone', 'mobile', 'roleName', 'enabled', 'actions'];
  displayColumnsService: string[] = ['name', 'key', 'secret', 'notes', 'enabled', 'actions'];
  dataSource;
  dataSourceService;
  isSelected = false;

  userAccounts: UserAccount[];
  serviceAccounts: ServiceAccount[];
  selectedAccount: Account;

  userAccountsTemp: UserAccount[];
  serviceAccountsTemp: ServiceAccount[];
  selectedAccountTemp: Account;

  accounts: ValueIdPair[];
  userAccountLength: number;
  serviceAccountLength: number;
  searchKey: string;
  searchKeyService: string;

  operatorRoleName: string = GRSSUserRoles.Operator;

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('pgUserAccounts', { read: MatPaginator }) pgUserAccounts: MatPaginator;
  @ViewChild('pgServiceAccounts', { read: MatPaginator }) pgServiceAccounts: MatPaginator;


  constructor(@Inject('BASE_URL') private baseUrl: string, private http: HttpClient, private dialog: MatDialog, public service: AppService, private translate: TranslateService) {
    this.service.SetTranslation('administration/manage-accounts');
   }

   // @HostListener allows us to also guard against browser refresh, close, etc.
  @HostListener('window:beforeunload') // , ['$event']) beforeUnloadHander(event: any) {
  //   if (!this.canDeactivate()) {
  //     this.dialog.open(DialogConfirmComponent, {
  //       disableClose: true,
  //       data: {
  //         title: 'WARNING: Unsaved Changes',
  //         message: 'Do you need to leave the page despite these changes?',
  //         innerHTML: true
  //       },
  //       position: { top: '5%' }
  //     }).afterClosed().subscribe((res: boolean) => {
  //       return res;
  //     });
  //   }
  // }
  canDeactivate(): Observable<boolean> | boolean {
    // insert logic to check if there are pending changes here;
    // returning true will navigate without confirmation
    // returning false will show a confirm dialog before navigating away

    let hasChanges: boolean = false;

    if (JSON.stringify(this.userAccounts) !== JSON.stringify(this.userAccountsTemp)) {
      hasChanges = true;
    }

    if (JSON.stringify(this.selectedAccount) !== JSON.stringify(this.selectedAccountTemp)) {
      hasChanges = true;
    }

    if (JSON.stringify(this.serviceAccounts) !== JSON.stringify(this.serviceAccountsTemp)) {
      hasChanges = true;
    }

    return !hasChanges;
  }

  ngOnInit() {
    this.userAccountLength = 0;
    this.serviceAccountLength = 0;
    this.GetAccounts();
  }

  /** Gets all the accounts from the server */
  GetAccounts() {
    // document.cookie = '_acc=; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    this.http.get<ValueIdPair[]>(this.baseUrl + 'api/account/getAccounts').subscribe(result => {
      this.accounts = result;
    });
  }

  /**
   *  Gets the selected account details
   * @param accountId selected account id
   */
  GetAccount(accountId: string) {
    this.http.get<Account>(this.baseUrl + 'api/account/getAccount/' + accountId).subscribe(result => {
      // document.cookie = '_acc=; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      // document.cookie = '_acc=' + result.accountId;
      this.isSelected = true;
      this.selectedAccount = result;
      this.selectedAccountTemp = JSON.parse(JSON.stringify(result)); // convert to json and again convert to interface - avoid changing both temp and the original property when users changes the original property.
    });
  }

  /**
   * Gets all the user accounts for selected account
   * @param accountId Selected account ID
   */
  GetUserAccounts(accountId: string) {
    this.http.get<UserAccount[]>(this.baseUrl + 'api/account/getUserAccounts/' + accountId).subscribe(result => {
      this.userAccounts = result;
      this.userAccountsTemp = JSON.parse(JSON.stringify(result));
      this.UpdateUserTable();
      if (this.service.currentUser.userRole === GRSSUserRoles.Compliance) {
        if (this.displayColumns.includes('actions')) {
          const index = this.displayColumns.indexOf('actions');
          this.displayColumns.splice(index, 1);
        }
      }
    });
  }

  /**
   * Gets all the service account for selected account
   * @param accountId Selected account ID
   */
  GetServiceAccounts(accountId: string) {
    this.http.get<ServiceAccount[]>(this.baseUrl + 'api/account/getServiceAccounts/' + accountId).subscribe(result => {
      this.serviceAccounts = result;
      this.serviceAccountsTemp = JSON.parse(JSON.stringify(result));
      this.UpdateServiceTable();
      if (this.service.currentUser.userRole === GRSSUserRoles.Compliance) {
        if (this.displayColumnsService.includes('actions')) {
          const index = this.displayColumnsService.indexOf('actions');
          this.displayColumnsService.splice(index, 1);
        }
      }
    });
  }

  /** Create new button event for account */
  OnCreateAccount() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.position = { top: '5%' };
    this.dialog.open(AddAccountComponent, dialogConfig).afterClosed().subscribe(res => {
      if (res === 1) {
        this.selectedAccount = null;
        this.selectedAccountTemp = null;
        this.isSelected = false;
        this.GetAccounts();
      }
    });
  }

  /**
   * Fires when user changes the account
   * @param account Changed account
   */
  OnChangeAccount(accountId: string) {
    this.GetAccount(accountId);
    this.GetUserAccounts(accountId);
    this.GetServiceAccounts(accountId);
  }

  /** Delete button event for the account */
  OnDeleteClick() {
    if (this.service.currentUser.userRole !== GRSSUserRoles.Operator) {
      this.dialog.open(DialogConfirmComponent, {
        disableClose: true,
        data: {
          title: this.translate.instant('Accounts.Messages.msgHeaderDeleteAcc'),
          message: this.translate.instant('Accounts.Messages.msgContentDeleteAcc')
        },
        position: { top: '5%' }
      }).afterClosed().subscribe(result => {
        if (result === true) {
          this.service.showSpinner();
          this.http.delete<GRSSResponse>(this.baseUrl + 'api/account/deleteAccount/' + this.selectedAccount.accountId).subscribe(res => {
            if (res.statusCode === 4) {
              this.service.SaveStatus(true, 'Accounts.Messages.msgHeaderDeleteAcc', res.statusMessage);
              // this.service.SaveStatus(true, 'Delete Account', res.statusMessage);
            } else {
              if (res.statusCode === 1) {
                this.isSelected = false;
                this.GetAccounts();
              }
              this.service.SaveStatus(false, 'Accounts.Messages.msgHeaderDeleteAcc', res.statusMessage);
              // this.service.SaveStatus(false, 'Delete Account', res.statusMessage);
            }
          }).add(() => {
            this.service.hideSpinner();
          });
        }
      });
    }
  }

  /** Save button event for the account */
  OnSaveClick() {
    if (this.selectedAccount.organisation) {
      this.service.showSpinner();
      this.http.put<GRSSResponse>(this.baseUrl + 'api/account/updateAccount', this.selectedAccount).subscribe(accResult => {
        if (accResult.statusCode === 1) {
          this.http.put<GRSSResponse>(this.baseUrl + 'api/account/upadteUserAccounts/' + this.selectedAccount.accountId, this.userAccounts).subscribe(userResult => {
            if (userResult.statusCode === 1) {
              this.http.put<GRSSResponse>(this.baseUrl + 'api/account/upadteServiceAccounts/' + this.selectedAccount.accountId, this.serviceAccounts).subscribe(serviceResult => {
                if (serviceResult.statusCode === 1) {
                  this.GetAccounts();
                  this.OnChangeAccount(this.selectedAccount.accountId);
                  this.service.SaveStatus(false, 'Accounts.Messages.msgHeaderSaveAcc', serviceResult.statusMessage);
                  // this.service.SaveStatus(false, 'Save Account', serviceResult.statusMessage);
                } else {
                  this.service.SaveStatus(false, 'Accounts.Messages.msgHeaderSaveAcc', serviceResult.statusMessage);
                  // this.service.SaveStatus(false, 'Save Account', serviceResult.statusMessage);
                }
              });
            } else {
              this.service.SaveStatus(false, 'Accounts.Messages.msgHeaderSaveAcc', userResult.statusMessage);
              // this.service.SaveStatus(false, 'Save Account', userResult.statusMessage);
            }
          });
        } else if (accResult.statusCode === 4) {
          this.service.SaveStatus(true, 'Accounts.Messages.msgHeaderSaveAcc', accResult.statusMessage);
          // this.service.SaveStatus(true, 'Save Account', accResult.statusMessage);
        } else {
          this.service.SaveStatus(false, this.translate.instant('Accounts.Messages.msgHeaderSaveAcc'), accResult.statusMessage, true);
        }
      }).add(() => {
        this.service.hideSpinner();
      });
    }
  }

  /**
   * Checking the token status and display status message
   * @param inValidToken Bool indicates the token status
   * @param message Status message
   */
  // SaveStatus(inValidToken: boolean, message: string) {
  //   if (inValidToken === false) {
  //     this.dialog.open(DialogStatusComponent, {
  //       disableClose: true,
  //       data: {
  //         title: 'Save Account',
  //         message: message,
  //         innerHTML: true
  //       },
  //       position: { top: '5%' }
  //     });
  //   } else {
  //     this.dialog.open(DialogStatusComponent, {
  //       disableClose: true,
  //       data: {
  //         title: 'Save Account',
  //         message: message,
  //         innerHTML: true
  //       },
  //       position: { top: '5%' }
  //     }).afterClosed().subscribe(res => {
  //       this.service.currentUser.isAuthenticated = false;
  //       this.router.navigate(['login']);
  //     });
  //   }
  // }

  /** Create new event for the user account */
  OnCreateUser() {
    this.service.isUpdateUserAccount = false;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '60%';
    dialogConfig.position = { top: '5%' };
    this.dialog.open(AddUserAccountComponent, dialogConfig).afterClosed().subscribe((res: UserAccount) => {
      if (res !== null) {
        this.userAccounts.push(res);
        this.UpdateUserTable();
      }
    });
  }

  /**
   * Edit button event for the user
   * @param user User account thet needs to be edited
   */
  OnEditUser(user: UserAccount) {
    this.service.isUpdateUserAccount = true;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '60%';
    dialogConfig.position = { top: '5%' };
    dialogConfig.data = user;
    this.dialog.open(AddUserAccountComponent, dialogConfig).afterClosed().subscribe((res: UserAccount) => {
      if (res !== null) {
        const index = this.userAccounts.indexOf(user, 0);
        this.userAccounts.splice(index, 1, res);
        this.UpdateUserTable();
      }
    });
  }

  /**
   * Delete button event for the user account
   * @param user User account that needs to be deleted
   */
  OnDeleteUser(user: UserAccount) {
    // Ask the user to confirm deleting the entity.
    const dialogRef = this.dialog.open(DialogConfirmComponent, {
      disableClose: true,
      data: {
        title: this.translate.instant('Accounts.Messages.msgHeaderDeleteUser'),
        message: this.translate.instant('Accounts.Messages.msgContentDeleteUser')
      },
      position: { top: '5%' }
    });
    dialogRef.afterClosed().subscribe(result => {
      // Evaluate the users response.
      if (result === true) {
        const index = this.userAccounts.indexOf(user, 0);
        this.userAccounts.splice(index, 1);
        this.UpdateUserTable();
      }
    });
  }

  /** Updating the user table */
  UpdateUserTable() {
    this.userAccountLength = this.userAccounts.length;
    this.dataSource = new MatTableDataSource(this.userAccounts);
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.pgUserAccounts;
  }

  /**
   * On click event for check box in user accounts table
   * @param user Approptiate user account
   */
  OnClickEnabledUser(user: UserAccount) {
    if (this.service.currentUser.isReadOnly === false) {
      const accountIndex = this.userAccounts.findIndex(x => x === user);
      this.userAccounts[accountIndex].enabled = !user.enabled;
      this.UpdateUserTable();
    }
  }

  /** applying filter for dataSources */
  ApplyFilter(no: number) {
    if (no === 0) {
      this.dataSource.filter = this.searchKey.trim().toLowerCase();
    } else if (no === 1) {
      this.dataSourceService.filter = this.searchKeyService.trim().toLowerCase();
    }
  }

  /** clear and close search user and set to the initial userlist by calling applyfilter */
  OnSearchClear(no: number) {
    this.ApplyFilter(no);
    if (no === 0) {
      this.searchKey = '';
    } else if (no === 1) {
      this.searchKeyService = '';
    }
  }

  /** Create new service account event */
  OnCreateService() {
    this.service.isUpdateServiceAccount = false;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '60%';
    dialogConfig.position = { top: '5%' };
    this.dialog.open(AddServiceAccountComponent, dialogConfig).afterClosed().subscribe((res: ServiceAccount) => {
      if (res !== null) {
        this.serviceAccounts.push(res);
        this.UpdateServiceTable();
      }
    });
  }

  /** Edit button event for thr service */
  OnEditService(service: ServiceAccount) {
    this.service.isUpdateServiceAccount = true;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '60%';
    dialogConfig.position = { top: '5%' };
    dialogConfig.data = service;
    this.dialog.open(AddServiceAccountComponent, dialogConfig).afterClosed().subscribe((res: ServiceAccount) => {
      if (res !== null) {
        const index = this.serviceAccounts.indexOf(service, 0);
        this.serviceAccounts.splice(index, 1, res);
        this.UpdateServiceTable();
      }
    });
  }

  /**
   * Delete button event for the service account
   * @param service service account details for delete
   */
  OnDeleteService(service: ServiceAccount) {
    const dialogRef = this.dialog.open(DialogConfirmComponent, {
      disableClose: true,
      data: {
        title: this.translate.instant('Accounts.Messages.msgHeaderDeleteService'),
        message: this.translate.instant('Accounts.Messages.msgContentDeleteService')
      },
      position: { top: '5%' }
    });
    dialogRef.afterClosed().subscribe(result => {
      // Evaluate the users response.
      if (result === true) {
        const index = this.serviceAccounts.indexOf(service, 0);
        this.serviceAccounts.splice(index, 1);
        this.UpdateServiceTable();
      }
    });
  }

  /** Updating the service account table */
  UpdateServiceTable() {
    this.serviceAccountLength = this.serviceAccounts.length;
    this.dataSourceService = new MatTableDataSource(this.serviceAccounts);
    this.dataSourceService.paginator = this.pgServiceAccounts;
  }

  /**
   *  Enabled checkbox click event in service account table
   * @param service service account details for enable
   */
  OnClickEnabledService(service: ServiceAccount) {
    if (this.service.currentUser.isReadOnly === false) {
      const accountIndex = this.serviceAccounts.findIndex(x => x === service);
      this.serviceAccounts[accountIndex].enabled = !service.enabled;
      this.UpdateServiceTable();
    }
  }

}

/** Interface to access account details */
interface Account {
  accountId: string;
  code: string;
  organisation: string;
  enabled: boolean;
}

/** Interface to access user account details */
interface UserAccount {
  userId: string;
  generalMarketIds: string[];
  username: string;
  password: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  mobile: string;
  roleId: string;
  roleName: string;
  roleIds: string[];
  enabled: boolean;
  twoFactor: boolean;
  challenge: boolean;
  challengeQuestion: string;
  challengeAnswer: string;
}

/** Interface to access service account details */
interface ServiceAccount {
  serviceId: string;
  generalMarketIds: string[];
  isRequestedKey: boolean;
  isRequestedSecret: boolean;
  name: string;
  key: string;
  secret: string;
  notes: string;
  enabled: boolean;
}

